import React, { useEffect, useMemo, useRef, useState } from "react";
import { Backdrop, Box, Grid, TextField, Typography } from "@material-ui/core";
import { useAppDispatch, useAppSelector } from "src/app-redux/hooks";
import {
  selectAllAgent,
  selectAllPartyNameRowsList,
} from "src/app-redux/configuration/configurationSlice";
import {
  getAllAgents,
  getAllPartName,
} from "src/app-redux/configuration/actions/configurationActions";
import CommonButton from "src/components/CommonButton/CommonButton";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import { fetchWrapper } from "src/api-client/fetchWrapper";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import CommonTextFieldDate from "src/components/CommonTextFieldForDate/CommonTextFieldDate";
import { containerLoadTypeList } from "src/common/utils/app.dropdowns";
import moment from "moment";
import { showNotification } from "src/common/utils/app.util";
import CommonLoader from "src/components/Loader/CommonLoader";
import { useStyles } from "./style";

const MANIFEST_CARGO_TYPE_FALLBACK_ID = "__manifest_cargo_type_fallback__";

const VIR_TYPE_OPTIONS = [
  { id: "BYD", name: "BYD" },
  { id: "Normal", name: "Normal" },
];

/** Match manifest / API cargo string to an item from vehicle configuration (name, id, code, etc.). */
function findCargoTypeOptionInList(options: any[], stored: string): any | null {
  const s = String(stored ?? "").trim();
  if (!s || !Array.isArray(options)) return null;
  const lower = s.toLowerCase();
  return (
    options.find((x) => String(x?.name ?? "").trim() === s) ??
    options.find(
      (x) =>
        String(x?.name ?? "")
          .trim()
          .toLowerCase() === lower
    ) ??
    options.find((x) => String(x?.id) === s) ??
    options.find((x) => String(x?.code ?? "").trim() === s) ??
    options.find(
      (x) =>
        String(x?.code ?? "")
          .trim()
          .toLowerCase() === lower
    ) ??
    options.find(
      (x) =>
        String(x?.shortName ?? "")
          .trim()
          .toLowerCase() === lower
    ) ??
    options.find((x) => String(x?.cargoTypeCode ?? "").trim() === s) ??
    null
  );
}

export function ManifestEntryForm(): React.ReactElement {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const allPartyNameRows = useAppSelector(selectAllPartyNameRowsList);
  const allAgents = useAppSelector(selectAllAgent);
  const initialValues = useMemo(
    () => ({
      tran_No: "",
      tran_Date: "",
      line: "",
      vessel: "",
      cargoType: "",
      cashClient: "",
      voyage: "",
      arrivalDate: "",
      forwarder: "",
      nominationId: "",
      agentId: "",
      delivery: "",
      stuffing: "",
      consigneeName: "",
      consigneeAddress: "",
      imO_Class: "",
      index_No: "",
      containerLoadType: "",
      isO_Code: "",
      viR_No: "",
      virType: "",
      pol: "",
      finalDest: "",
      marksAndNumbers: "",
      goodsDescription: "",
      hsCode: "",
      volumeM3: "",
      grossWeight: "",
      billNumber: "",
      netWeight: "",
    }),
    []
  );

  const [values, setValues] = useState<Record<string, string>>(initialValues);
  const [virOptions, setVirOptions] = useState<any[]>([]);
  const [virInputLength, setVirInputLength] = useState<number>(0);
  const [manifestInfo, setManifestInfo] = useState<any>(null);
  const [billNumberOptions, setBillNumberOptions] = useState<{ id: string; name: string }[]>([]);
  const [containerRows, setContainerRows] = useState<any[]>([]);
  const [cargoTypeOptions, setCargoTypeOptions] = useState<any[]>([]);
  const [consigneeList, setConsigneeList] = useState<{ id: number; name: string }[]>([]);
  const [filteredNominations, setFilteredNominations] = useState<any[]>([]);
  const [lockedKeys, setLockedKeys] = useState<Set<string>>(new Set());
  const [loader, setLoader] = useState(false);
  const manifestFetchGenRef = useRef(0);

  const tranDatePickerRef = useRef<any>(null);
  const arrivalDatePickerRef = useRef<any>(null);

  const alwaysLockIfAutofilled = new Set<string>(["index_No"]);
  const isEditableKey = (key: string) =>
    key === "viR_No" ||
    key === "forwarder" ||
    key === "nominationId" ||
    key === "agentId" ||
    key === "cargoType" ||
    key === "consigneeName";

  const getNominationsByParty = async (partyId: string | number) => {
    const numericId = Number(partyId);
    if (!partyId || !Number.isFinite(numericId) || numericId <= 0) {
      setFilteredNominations([]);
      return;
    }
    try {
      const response: any = await fetchWrapper.get(
        `get-party-nomination-type-by-id?PartyId=${numericId}`
      );
      if (response?.isSuccess) {
        setFilteredNominations(response.value?.nominationList || []);
      } else {
        setFilteredNominations([]);
      }
    } catch {
      setFilteredNominations([]);
    }
  };
  const isFieldDisabled = (key: string) => {
    if (!lockedKeys.has(key)) return false;
    if (isEditableKey(key)) return false;
    if (alwaysLockIfAutofilled.has(key)) return true;
    return true;
  };

  const getVirNumbers = async (virNo: string) => {
    const trimmed = String(virNo ?? "").trim();
    if (!trimmed) return;

    const results: any = await fetchWrapper.get(`search-vir-number/${trimmed}`);
    if (results?.isSuccess) setVirOptions(results.value?.virList ?? []);
  };

  useEffect(() => {
    dispatch(getAllPartName(true));
    dispatch(getAllAgents(true));
  }, [dispatch]);

  useEffect(() => {
    const forwarderId = String(values.forwarder ?? "").trim();
    if (forwarderId) {
      getNominationsByParty(forwarderId);
    } else {
      setFilteredNominations([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.forwarder]);

  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const cfg: any = await fetchWrapper.get("get-all-vehicle-configuration");
        if (cfg?.isSuccess) setCargoTypeOptions(cfg.value?.cargoTypeData ?? []);
      } catch {
        // ignore
      }
      try {
        const res: any = await fetchWrapper.get(`get_all-consignee-details?isActive=${true}`);
        if (res?.isSuccess) {
          const raw = res.value?.consigneeList ?? res.value;
          const list = Array.isArray(raw)
            ? raw.map((item: { id: number; name?: string }) => ({
                id: item.id,
                name: item.name ?? "",
              }))
            : [];
          setConsigneeList(list);
        }
      } catch {
        // ignore
      }
    };

    loadDropdowns();
  }, []);

  const { cargoTypeAutocompleteOptions, cargoTypeAutocompleteValue } = useMemo(() => {
    const stored = String(values.cargoType ?? "").trim();
    const base = Array.isArray(cargoTypeOptions) ? [...cargoTypeOptions] : [];
    if (!stored) {
      return { cargoTypeAutocompleteOptions: base, cargoTypeAutocompleteValue: null };
    }
    const matched = findCargoTypeOptionInList(base, stored);
    if (matched) {
      return { cargoTypeAutocompleteOptions: base, cargoTypeAutocompleteValue: matched };
    }
    const fallback = { name: stored, id: MANIFEST_CARGO_TYPE_FALLBACK_ID };
    base.push(fallback);
    return { cargoTypeAutocompleteOptions: base, cargoTypeAutocompleteValue: fallback };
  }, [cargoTypeOptions, values.cargoType]);

  const getManifestInfo = async (virNo: string, blNo?: string) => {
    const fetchGen = ++manifestFetchGenRef.current;
    setLoader(true);
    try {
      const qs = new URLSearchParams();
      qs.set("virNo", virNo);
      if (blNo) qs.set("blNo", blNo);

      const results: any = await fetchWrapper.get(`get-manifest-info?${qs.toString()}`);
      if (!results?.isSuccess) return;

      // API sometimes wraps data as { validationErrors: [], value: {...} }
      const raw = results?.value?.value ?? results?.value ?? null;
      const headerData = raw?.headerData ?? {};
      const gridData = Array.isArray(raw?.containerGridData) ? raw.containerGridData : [];

      setManifestInfo(raw);
      setContainerRows(gridData);
      const nextLocked = new Set<string>();
      Object.entries(headerData ?? {}).forEach(([k, v]) => {
        if (alwaysLockIfAutofilled.has(k)) {
          nextLocked.add(k);
          return;
        }
        if (Boolean(String(v ?? "").trim())) nextLocked.add(k);
      });
      // Ensure Index is locked after any API fill
      alwaysLockIfAutofilled.forEach((k) => nextLocked.add(k));
      if (headerData?.forwarderId != null) nextLocked.add("forwarder");
      if (headerData?.nominationId != null) nextLocked.add("nominationId");
      if (headerData?.agentId != null) nextLocked.add("agentId");
      if (headerData?.consigneeId != null) nextLocked.add("consigneeName");
      setLockedKeys(nextLocked);

      setValues((prev) => ({
        ...prev,
        tran_No: headerData?.tran_No == null ? "" : String(headerData.tran_No),
        tran_Date: headerData?.tran_Date == null ? "" : String(headerData.tran_Date),
        line: headerData?.line == null ? "" : String(headerData.line),
        vessel: headerData?.vessel == null ? "" : String(headerData.vessel),
        cargoType: headerData?.cargoType == null ? "" : String(headerData.cargoType),
        cashClient: headerData?.cashClient == null ? "" : String(headerData.cashClient),
        voyage: headerData?.voyage == null ? "" : String(headerData.voyage),
        arrivalDate: headerData?.arrivalDate == null ? "" : String(headerData.arrivalDate),
        forwarder: (() => {
          if (headerData?.forwarderId != null) return String(headerData.forwarderId);
          if (headerData?.forwarder != null) return String(headerData.forwarder);
          return "";
        })(),
        nominationId: headerData?.nominationId == null ? "" : String(headerData.nominationId),
        agentId: headerData?.agentId == null ? "" : String(headerData.agentId),
        delivery: headerData?.delivery == null ? "" : String(headerData.delivery),
        stuffing: headerData?.stuffing == null ? "" : String(headerData.stuffing),
        consigneeName: (() => {
          if (headerData?.consigneeId != null) return String(headerData.consigneeId);
          const raw = headerData?.consigneeName;
          if (raw == null) return "";
          const str = String(raw).trim();
          const byId = consigneeList.find((c) => String(c.id) === str);
          if (byId) return String(byId.id);
          const byName = consigneeList.find((c) => c.name.toLowerCase() === str.toLowerCase());
          if (byName) return String(byName.id);
          return str;
        })(),
        consigneeAddress:
          headerData?.consigneeAddress == null ? "" : String(headerData.consigneeAddress),
        imO_Class: headerData?.imO_Class == null ? "" : String(headerData.imO_Class),
        index_No: headerData?.index_No == null ? prev.index_No : String(headerData.index_No),
        containerLoadType:
          headerData?.containerLoadType == null ? "" : String(headerData.containerLoadType),
        isO_Code: headerData?.isO_Code == null ? "" : String(headerData.isO_Code),
        viR_No: headerData?.viR_No == null ? prev.viR_No : String(headerData.viR_No),
        virType: headerData?.virType == null ? "" : String(headerData.virType),
        pol: headerData?.pol == null ? "" : String(headerData.pol),
        finalDest: headerData?.finalDest == null ? "" : String(headerData.finalDest),
        marksAndNumbers:
          headerData?.marksAndNumbers == null ? "" : String(headerData.marksAndNumbers),
        goodsDescription:
          headerData?.goodsDescription == null ? "" : String(headerData.goodsDescription),
        hsCode: headerData?.hsCode == null ? "" : String(headerData.hsCode),
        volumeM3: headerData?.volumeM3 == null ? "" : String(headerData.volumeM3),
        grossWeight: headerData?.grossWeight == null ? "" : String(headerData.grossWeight),
        netWeight: headerData?.netWeight == null ? "" : String(headerData.netWeight),
      }));
    } catch {
      // network / server errors — loader cleared in finally
    } finally {
      if (fetchGen === manifestFetchGenRef.current) {
        setLoader(false);
      }
    }
  };

  const getPreGateInVirData = async (virNo: string) => {
    setLoader(true);
    try {
      const results: any = await fetchWrapper.get(`get-pre-gate-in-vir-data/${virNo}`);
      if (results?.isSuccess) {
        const data = Array.isArray(results.value) ? results.value : [];
        const uniqueBills = Array.from(
          new Set(data.map((item: any) => String(item?.billNumber ?? "").trim()).filter(Boolean))
        ).map((bn) => ({ id: bn as string, name: bn as string }));
        setBillNumberOptions(uniqueBills);
      }
    } catch {
      // ignore
    } finally {
      setLoader(false);
    }
  };

  const toNumberOrZero = (v: any) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const toIsoStringOrNull = (v: any) => {
    const raw = String(v ?? "").trim();
    if (!raw) return null;
    const m = moment(raw, ["DD/MM/YYYY hh:mm A", "D/M/YYYY h:mm A", moment.ISO_8601], true);
    if (m.isValid()) return m.toISOString();
    return raw; // fallback: send as-is if backend accepts
  };

  const handleSave = async () => {
    const virNo = String(values.viR_No ?? "").trim();
    if (!virNo) {
      showNotification("Please select VIR Number", "error", dispatch);
      return;
    }
    if (!String(values.virType ?? "").trim()) {
      showNotification("Please select VIR Type", "error", dispatch);
      return;
    }
    if (!String(values.billNumber ?? "").trim()) {
      showNotification("Please select Bill Number", "error", dispatch);
      return;
    }
    if (!String(values.vessel ?? "").trim()) {
      showNotification("Please enter Vessel", "error", dispatch);
      return;
    }
    if (!String(values.voyage ?? "").trim()) {
      showNotification("Please enter Voyage", "error", dispatch);
      return;
    }

    const indexNo = values.index_No ? Number.parseInt(String(values.index_No), 10) : 0;
    const terminalIdRaw = Number(localStorage.getItem("terminalId"));
    const terminalId = Number.isNaN(terminalIdRaw) ? 0 : terminalIdRaw;
    const payload = {
      virNo,
      virType: String(values.virType ?? "").trim(),
      indexNo: Number.isFinite(indexNo) ? indexNo : 0,
      blNo: String(values.billNumber ?? "").trim(),
      terminalId,
      tranDate: toIsoStringOrNull(values.tran_Date),
      line: String(values.line ?? "").trim(),
      vessel: String(values.vessel ?? "").trim(),
      voyage: String(values.voyage ?? "").trim(),
      cargoType: String(values.cargoType ?? "").trim(),
      containerLoadType: String(values.containerLoadType ?? "").trim(),
      arrivalDate: toIsoStringOrNull(values.arrivalDate),
      forwarderId: values.forwarder ? Number(values.forwarder) || 0 : 0,
      nominationId: values.nominationId ? Number(values.nominationId) || 0 : 0,
      agentId: values.agentId ? Number(values.agentId) || 0 : 0,
      cashClient: String(values.cashClient ?? "").trim(),
      delivery: String(values.delivery ?? "").trim(),
      stuffing: String(values.stuffing ?? "").trim(),
      consigneeId: values.consigneeName ? Number(values.consigneeName) || 0 : 0,
      consigneeAddress: String(values.consigneeAddress ?? "").trim(),
      imoClass: String(values.imO_Class ?? "").trim(),
      isoCode: String(values.isO_Code ?? "").trim(),
      pol: String(values.pol ?? "").trim(),
      finalDest: String(values.finalDest ?? "").trim(),
      marksAndNumbers: String(values.marksAndNumbers ?? "").trim(),
      goodsDescription: String(values.goodsDescription ?? "").trim(),
      hsCode: String(values.hsCode ?? "").trim(),
      volumeM3: toNumberOrZero(values.volumeM3),
      grossWeight: toNumberOrZero(values.grossWeight),
      netWeight: toNumberOrZero(values.netWeight),
      containers: (containerRows ?? []).map((r: any) => ({
        containerNo: String(r?.containerNo ?? "").trim(),
        sealNo: String(r?.sealNo ?? "").trim(),
        volumeM3: toNumberOrZero(r?.volumeM3),
        packages: toNumberOrZero(r?.packages),
        packageType: String(r?.packageType ?? "").trim(),
        grossWeight: toNumberOrZero(r?.grossWeight),
      })),
    };

    setLoader(true);
    try {
      let res: any;
      try {
        res = await fetchWrapper.put("save-manifest-info", payload);
      } catch (err: any) {
        const status = err?.response?.status;
        if (status) throw err;
        throw err;
      }

      if (res?.isSuccess) {
        showNotification(res?.successMessage || "Manifest saved successfully", "success", dispatch);
        return;
      }

      const msg =
        res?.error ||
        res?.message ||
        (Array.isArray(res?.validationErrors) && res.validationErrors.length
          ? res.validationErrors.map((x: any) => x?.message ?? x).join(", ")
          : "Failed to save manifest");
      showNotification(String(msg), "error", dispatch);
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to save manifest";
      showNotification(String(msg), "error", dispatch);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    const virNo = String(values.viR_No ?? "").trim();
    setBillNumberOptions([]);
    setValues((prev) => ({ ...initialValues, viR_No: prev.viR_No }));
    setManifestInfo(null);
    setContainerRows([]);
    setLockedKeys(new Set());
    if (!virNo) return;

    const t = window.setTimeout(() => {
      getPreGateInVirData(virNo);
    }, 350);

    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.viR_No]);

  useEffect(() => {
    const virNo = String(values.viR_No ?? "").trim();
    const billNo = String(values.billNumber ?? "").trim();
    if (!virNo || !billNo) return;

    const t = window.setTimeout(() => {
      getManifestInfo(virNo, billNo);
    }, 350);

    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.billNumber]);

  const fields = useMemo(
    () =>
      [
        { name: "viR_No", label: "VIR Number", required: true },
        { name: "billNumber", label: "Bill Number", required: true },
        { name: "virType", label: "VIR Type", required: true },
        { name: "containerLoadType", label: "Container Load Type" },
        { name: "cargoType", label: "Cargo Type" },
        { name: "forwarder", label: "Forwarder" },
        { name: "nominationId", label: "Nomination Name" },
        { name: "agentId", label: "Agent Name" },
        { name: "index_No", label: "Index Number" },
        { name: "tran_No", label: "Tran Number" },
        { name: "tran_Date", label: "Tran Date" },
        { name: "line", label: "Line" },
        { name: "vessel", label: "Vessel", required: true },
        { name: "cashClient", label: "Cash Client" },
        { name: "voyage", label: "Voyage", required: true },
        { name: "arrivalDate", label: "Arrival Date" },
        { name: "delivery", label: "Delivery" },
        { name: "stuffing", label: "Stuffing" },
        { name: "consigneeName", label: "Consignee Name" },
        { name: "consigneeAddress", label: "Consignee Address" },
        { name: "imO_Class", label: "IMO Class" },
        { name: "isO_Code", label: "ISO Code" },
        { name: "pol", label: "POL" },
        { name: "finalDest", label: "Final Dest" },
        { name: "marksAndNumbers", label: "Marks And Numbers" },
        { name: "goodsDescription", label: "Goods Description" },
        { name: "hsCode", label: "HS Code" },
        { name: "volumeM3", label: "Volume (m3)" },
        { name: "grossWeight", label: "Gross Weight" },
        { name: "netWeight", label: "Net Weight" },
      ] as { name: string; label: string; required?: boolean }[],
    []
  );

  const containerColumns = useMemo(
    () => [
      { accessorKey: "sNo", header: "S No", size: 80 },
      { accessorKey: "containerNo", header: "Container No", size: 170 },
      { accessorKey: "sealNo", header: "Seal No", size: 140 },
      { accessorKey: "sizeType", header: "Size Type", size: 120 },
      { accessorKey: "packages", header: "Packages", size: 110 },
      { accessorKey: "packageType", header: "Package Type", size: 160 },
      { accessorKey: "grossWeight", header: "Gross Weight", size: 130 },
      { accessorKey: "volumeM3", header: "Volume (m3)", size: 130 },
      { accessorKey: "unit", header: "Unit", size: 90 },
    ],
    []
  );

  return (
    <Grid className={classes.UserMainPanel}>
      <Backdrop className={classes.backdrop} open={loader}>
        <CommonLoader />
      </Backdrop>
      <Grid container alignItems="center" spacing={2} className={classes.formRow}>
        <Grid item xs={12} md={6}>
          <Typography className={classes.inlineHeading}>Manifest Entry:</Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box textAlign="right" mb={2} mt={1}>
            <CommonButton
              cssClass="customSubmitBtn"
              onClick={() => {
                manifestFetchGenRef.current += 1;
                setLoader(false);
                setValues(initialValues);
                setManifestInfo(null);
                setBillNumberOptions([]);
                setContainerRows([]);
                setFilteredNominations([]);
                setLockedKeys(new Set());
              }}
            >
              Reset
            </CommonButton>
            <CommonButton
              isGreen
              cssClass="customSubmitBtn"
              disable={loader}
              onClick={() => {
                handleSave();
              }}
            >
              Save
            </CommonButton>
          </Box>
        </Grid>
      </Grid>

      <Grid container direction="row" alignItems="center" spacing={2}>
        {fields.map((f) => (
          <Grid key={f.name} item xs={12} md={3}>
            <Box>
              {f.name === "viR_No" ? (
                <CommonAutoCompleteField
                  cssClassName="customTextField"
                  label="VIR Number"
                  placeholder="Select VIR Number"
                  name="viR_No"
                  required={true}
                  optionsType={virOptions}
                  getOptionLabel={(option: any) => option?.virNo || ""}
                  value={virOptions.find((x: any) => x?.virNo === (values.viR_No ?? "")) || null}
                  disabled={false}
                  onChange={(option: any) => {
                    setValues((prev) => ({
                      ...prev,
                      viR_No: option?.virNo || "",
                    }));
                  }}
                  onKeyUp={(e: any) => {
                    const nextLen = e.target.value.length;
                    setVirInputLength(nextLen);
                    if (nextLen === 2 && virInputLength < 2) getVirNumbers(e.target.value);
                  }}
                  onKeyDownCapture={(e: any) => {
                    if (/[^A-Za-z0-9\d]/.test(e.key)) e.preventDefault();
                  }}
                  onPaste={(e: any) => {
                    const pasted = e.clipboardData.getData("text");
                    if (/[^A-Za-z0-9\d]/.test(pasted)) {
                      e.preventDefault();
                      return;
                    }
                    if (pasted.length >= 2) getVirNumbers(pasted);
                  }}
                />
              ) : f.name === "virType" ? (
                <CommonAutoCompleteField
                  cssClassName="customTextField"
                  label="VIR Type"
                  placeholder="Select VIR Type"
                  name="virType"
                  required={true}
                  optionsType={VIR_TYPE_OPTIONS}
                  getOptionLabel={(option: any) => option?.name || ""}
                  value={VIR_TYPE_OPTIONS.find((x) => x.id === values.virType) || null}
                  disabled={false}
                  onChange={(option: any) => {
                    setValues((prev) => ({
                      ...prev,
                      virType: option?.id || "",
                    }));
                  }}
                />
              ) : f.name === "billNumber" ? (
                <CommonAutoCompleteField
                  cssClassName="customTextField"
                  label="Bill Number"
                  placeholder="Select Bill Number"
                  name="billNumber"
                  required={true}
                  optionsType={billNumberOptions}
                  getOptionLabel={(option: any) => option?.name || ""}
                  value={billNumberOptions.find((x) => x.id === values.billNumber) || null}
                  disabled={!billNumberOptions.length}
                  onChange={(option: any) => {
                    setValues((prev) => ({
                      ...prev,
                      billNumber: option?.id || "",
                    }));
                  }}
                />
              ) : f.name === "tran_Date" ? (
                <CommonTextFieldDate
                  cssClassName={`${classes.dateTime} ${"customTextField"}`}
                  outerCss={true}
                  datePickerRef={tranDatePickerRef}
                  label={"Tran Date"}
                  name={"tran_Date"}
                  required={false}
                  value={values.tran_Date}
                  onChange={(v: any) => {
                    setValues((prev) => ({
                      ...prev,
                      tran_Date: v,
                    }));
                  }}
                  isReadOnly={isFieldDisabled("tran_Date")}
                />
              ) : f.name === "arrivalDate" ? (
                <CommonTextFieldDate
                  cssClassName={`${classes.dateTime} ${"customTextField"}`}
                  outerCss={true}
                  datePickerRef={arrivalDatePickerRef}
                  label={"Arrival Date"}
                  name={"arrivalDate"}
                  required={false}
                  value={values.arrivalDate}
                  onChange={(v: any) => {
                    setValues((prev) => ({
                      ...prev,
                      arrivalDate: v,
                    }));
                  }}
                  isReadOnly={isFieldDisabled("arrivalDate")}
                />
              ) : f.name === "cargoType" ? (
                <CommonAutoCompleteField
                  cssClassName="customTextField"
                  label="Cargo Type"
                  placeholder="Select Cargo Type"
                  name="cargoType"
                  optionsType={cargoTypeAutocompleteOptions}
                  getOptionLabel={(option: any) => option?.name || ""}
                  value={cargoTypeAutocompleteValue}
                  disabled={isFieldDisabled("cargoType")}
                  onChange={(option: any) => {
                    setValues((prev) => ({
                      ...prev,
                      cargoType: option?.name || "",
                    }));
                  }}
                />
              ) : f.name === "containerLoadType" ? (
                <CommonAutoCompleteField
                  cssClassName="customTextField"
                  label="Container Load Type"
                  placeholder="Select Container Load Type"
                  name="containerLoadType"
                  optionsType={containerLoadTypeList}
                  getOptionLabel={(option: any) => option || ""}
                  value={
                    containerLoadTypeList.find((st: any) => st === values.containerLoadType) || null
                  }
                  disabled={isFieldDisabled("containerLoadType")}
                  onChange={(option: any) => {
                    setValues((prev) => ({
                      ...prev,
                      containerLoadType: option || "",
                    }));
                  }}
                />
              ) : f.name === "consigneeName" ? (
                <CommonAutoCompleteField
                  cssClassName="customTextField"
                  label="Consignee Name"
                  placeholder="Select Consignee"
                  name="consigneeName"
                  optionsType={consigneeList}
                  getOptionLabel={(option: any) => option?.name || ""}
                  value={
                    Array.isArray(consigneeList)
                      ? consigneeList.find((c) => String(c.id) === String(values.consigneeName)) ||
                        null
                      : null
                  }
                  disabled={isFieldDisabled("consigneeName")}
                  onChange={(option: any) => {
                    setValues((prev) => ({
                      ...prev,
                      consigneeName: option ? String(option.id) : "",
                    }));
                  }}
                />
              ) : f.name === "forwarder" ? (
                <CommonAutoCompleteField
                  cssClassName="customTextField"
                  label="Forwarder"
                  placeholder="Select Forwarder Name"
                  name="forwarder"
                  optionsType={allPartyNameRows as any[]}
                  getOptionLabel={(option: any) => option?.name || ""}
                  value={
                    (allPartyNameRows as any[]).find(
                      (x: any) => String(x?.id) === String(values.forwarder)
                    ) || null
                  }
                  disabled={isFieldDisabled("forwarder")}
                  onChange={(option: any) => {
                    setValues((prev) => ({
                      ...prev,
                      forwarder: String(option?.id ?? ""),
                      nominationId: "",
                    }));
                  }}
                />
              ) : f.name === "nominationId" ? (
                <CommonAutoCompleteField
                  cssClassName="customTextField"
                  label="Nomination Name"
                  placeholder="Select Nomination"
                  name="nominationId"
                  optionsType={filteredNominations}
                  getOptionLabel={(option: any) => option?.name || ""}
                  value={
                    filteredNominations.find(
                      (n: any) =>
                        String(n?.nominationId ?? n?.id ?? "") === String(values.nominationId)
                    ) || null
                  }
                  disabled={
                    isFieldDisabled("nominationId") || !String(values.forwarder ?? "").trim()
                  }
                  onChange={(option: any) => {
                    const id = option?.nominationId ?? option?.id;
                    setValues((prev) => ({
                      ...prev,
                      nominationId: id != null && id !== "" ? String(id) : "",
                    }));
                  }}
                />
              ) : f.name === "agentId" ? (
                <CommonAutoCompleteField
                  cssClassName="customTextField"
                  label="Agent Name"
                  placeholder="Select Agent"
                  name="agentId"
                  optionsType={allAgents as any[]}
                  getOptionLabel={(option: any) => option?.name || ""}
                  value={
                    (allAgents as any[]).find(
                      (a: any) => String(a?.id) === String(values.agentId)
                    ) || null
                  }
                  disabled={isFieldDisabled("agentId")}
                  onChange={(option: any) => {
                    setValues((prev) => ({
                      ...prev,
                      agentId: option ? String(option.id) : "",
                    }));
                  }}
                />
              ) : (
                <TextField
                  size="small"
                  fullWidth
                  className="customTextField"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  label={f.label}
                  name={f.name}
                  required={Boolean(f.required)}
                  id={f.name}
                  placeholder={`Enter ${f.label}`}
                  value={values[f.name] ?? ""}
                  disabled={isFieldDisabled(f.name)}
                  onChange={(e) =>
                    setValues((prev) => ({
                      ...prev,
                      [f.name]: e.target.value,
                    }))
                  }
                />
              )}
            </Box>
          </Grid>
        ))}
      </Grid>

      {Boolean(containerRows?.length) && (
        <Box mt={3}>
          <Typography className={classes.inlineHeading}>Container Grid:</Typography>
          <Box mt={1}>
            <CommonMaterialGrid
              rows={containerRows}
              columns={containerColumns}
              height={650}
              loader={false}
              hideFooter={false}
            />
          </Box>
        </Box>
      )}
    </Grid>
  );
}
